import {
  cardStateKey,
  db,
  formKey,
  makeDirection,
  parseDirection,
  type CardState,
  type Concept,
  type ConceptForm,
  type Direction,
} from "./db";
import type {
  AgeGroupId,
  CefrLevelId,
  EntityTypeId,
  LanguageId,
  TopicId,
} from "./references";
import type { SupportedUiLang } from "../i18n";
import { koreanPackMetadata } from "./packMetadataKo";
import { enqueueInTx, scheduleFlush } from "./sync/engine";

/**
 * Bilingual pack metadata fields, shared by {@link PackJson},
 * {@link PackRegistryEntry}, and `StarterDeck` (see starterDecks/types.ts).
 *
 * `name`/`description` stay as plain strings — the original single-locale
 * value, whichever language a given pack happened to be authored in — so
 * clients/caches still on the pre-localization schema keep reading a plain
 * string with no code changes (see {@link localizedPackName} for the read
 * side of that compat guarantee). `*_en`/`*_ru` only carry the *missing*
 * side of the translation: a pack authored in Russian gets `name_en`, one
 * authored in English gets `name_ru`. Never both — the language `name`
 * itself is already in doesn't need a redundant translation.
 */
export type PackLocalizedText = {
  id?: string;
  name: string;
  name_en?: string;
  name_ru?: string;
  name_ko?: string;
  description?: string;
  description_en?: string;
  description_ru?: string;
  description_ko?: string;
};

/** Resolve a pack's display name for the given UI language, falling back to
    the untranslated base string (covers both "no translation recorded yet"
    and "stale cached registry entry from before this field existed"). */
export function localizedPackName(entry: PackLocalizedText, lang: SupportedUiLang): string {
  if (lang === "ko") {
    return entry.name_ko ?? koreanPackMetadata(entry.id)?.name ?? entry.name;
  }
  return lang === "en"
    ? entry.name_en ?? entry.name
    : entry.name_ru ?? entry.name;
}

/** Same fallback logic as {@link localizedPackName}, for the description. */
export function localizedPackDescription(
  entry: PackLocalizedText,
  lang: SupportedUiLang,
): string | undefined {
  if (lang === "ko") {
    return (
      entry.description_ko ??
      koreanPackMetadata(entry.id)?.description ??
      entry.description
    );
  }
  return lang === "en"
    ? entry.description_en ?? entry.description
    : entry.description_ru ?? entry.description;
}

/**
 * On-disk JSON format for a content pack. Designed so a single file holds
 * concepts with forms in many languages — installation can pick the subset
 * of languages the user actually wants.
 */
export type PackJson = PackLocalizedText & {
  id: string;
  version: number;
  level?: CefrLevelId;
  topic?: TopicId;
  ageGroup?: AgeGroupId;
  concepts: PackConceptJson[];
};

export type PackConceptJson = {
  /** Stable id within the pack. Concept primary keys are namespaced to
      `${packId}:${conceptId}` so two packs can't collide. */
  id: string;
  entityType?: EntityTypeId;
  frequency?: number;
  topic?: TopicId;
  ageGroup?: AgeGroupId;
  level?: CefrLevelId;
  /** Surface forms keyed by language. First entry per language is canonical. */
  forms: Partial<Record<LanguageId, string[]>>;
};

/** Stored alongside settings to track which packs are present at which version. */
export type InstalledPack = {
  id: string;
  version: number;
  name: string;
  installedAt: number;
  /** Language subset the user chose at install time. */
  langs: LanguageId[];
  /**
   * Dictionary the pack was loaded into. Absent on rows written before packs
   * belonged to a deck at all — {@link adoptOrphanPacks} gives those a home.
   */
  deckId?: string;
  /** Explicit practice direction for rows written after the direction cutover. */
  direction?: Direction;
  /** Whether uninstall may remove the deck because this install created it. */
  ownsDeck?: boolean;
};

export type PackRegistryEntry = PackLocalizedText & {
  id: string;
  file: string;
  level?: CefrLevelId;
  languages: LanguageId[];
  conceptCount: number;
};

const REGISTRY_KEY = "packs:registry";
const INSTALLED_KEY = "packs:installed";
/** Cap on the network attempt in {@link loadPackRegistry} — on a bad
    connection we'd rather fall back to the cached copy quickly than hang. */
const REGISTRY_FETCH_TIMEOUT_MS = 2500;

/**
 * Fetch the bundled pack registry (public/packs/index.json). Network-first:
 * always tries the network so edits to index.json show up for returning
 * users, and only falls back to the last-known copy in `kv` when offline,
 * slow, or the request fails — that's what keeps the Library usable offline.
 */
export async function loadPackRegistry(): Promise<PackRegistryEntry[]> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REGISTRY_FETCH_TIMEOUT_MS);
    let res: Response;
    try {
      res = await fetch("/packs/index.json", { signal: controller.signal });
    } finally {
      clearTimeout(timeout);
    }
    if (!res.ok) throw new Error(`pack registry: HTTP ${res.status}`);
    const json = (await res.json()) as { packs: PackRegistryEntry[] };
    await db.kv.put({ id: REGISTRY_KEY, value: json.packs });
    return json.packs;
  } catch (err) {
    const cached = await db.kv.get(REGISTRY_KEY);
    if (cached) return cached.value as PackRegistryEntry[];
    throw err;
  }
}

export async function loadPackJson(file: string): Promise<PackJson> {
  const res = await fetch(`/packs/${file}`);
  if (!res.ok) throw new Error(`pack ${file}: HTTP ${res.status}`);
  return (await res.json()) as PackJson;
}

export async function listInstalledPacks(): Promise<InstalledPack[]> {
  const row = await db.kv.get(INSTALLED_KEY);
  return (row?.value as InstalledPack[] | undefined) ?? [];
}

async function writeInstalledPacks(packs: InstalledPack[]): Promise<void> {
  await db.kv.put({ id: INSTALLED_KEY, value: packs });
}

/**
 * Install (or upgrade) a pack into IndexedDB. Concepts and forms are upserted;
 * the install record is added to {@link listInstalledPacks}.
 *
 * `langs` filters which form rows are written — useful when the user only
 * studies a subset of the pack's languages and we don't want to bloat the
 * `conceptForms` table.
 *
 * `uiLang` picks which language {@link InstalledPack.name} is snapshotted in
 * (Dashboard/PackList show it as-is afterwards, not re-localized live).
 * Defaults to the pack's untranslated base string when omitted.
 */
/**
 * Where a pack's words land. A pack is never installed on its own any more:
 * it fills a dictionary the user already has, the same shape `installStarterDeck`
 * has always produced. Words that belong to no deck were the reason "Sets" and
 * "Decks" behaved like two unrelated apps.
 */
export type DirectionPair = { source: LanguageId; target: LanguageId };
export type PackTarget = { deckId?: string; createDeck?: boolean; pair: DirectionPair };

export function selectPackDirectionPair(
  supported: LanguageId[],
  preferred: DirectionPair,
): DirectionPair | null {
  if (
    preferred.source !== preferred.target &&
    supported.includes(preferred.source) &&
    supported.includes(preferred.target)
  ) {
    return preferred;
  }
  const [source, target] = [...new Set(supported)];
  return source && target ? { source, target } : null;
}

export function previewPackCards(
  pack: PackJson,
  pair: DirectionPair,
  limit: number,
): Array<{ conceptId: string; front: string; back: string }> {
  return pack.concepts
    .map((concept) => ({
      conceptId: `${pack.id}:${concept.id}`,
      front: concept.forms[pair.source]?.[0],
      back: concept.forms[pair.target]?.[0],
    }))
    .filter(
      (card): card is { conceptId: string; front: string; back: string } =>
        Boolean(card.front && card.back),
    )
    .slice(0, Math.max(0, limit));
}

function directionForPair(pair: DirectionPair): Direction {
  if (pair.source === pair.target) {
    throw new Error("pack direction needs two different languages");
  }
  return makeDirection(pair.source, pair.target);
}

export async function installPack(
  pack: PackJson,
  target: PackTarget,
  uiLang?: SupportedUiLang,
): Promise<{
  concepts: number;
  forms: number;
  cards: number;
  deckId: string;
  direction: Direction;
  cardKeys: string[];
}> {
  const direction = directionForPair(target.pair);
  const eligible = pack.concepts.filter((concept) =>
    Boolean(
      concept.forms[target.pair.source]?.length &&
      concept.forms[target.pair.target]?.length,
    ),
  );
  if (eligible.length === 0) {
    throw new Error("pack has no concepts with both selected language forms");
  }

  const concepts: Concept[] = eligible.map((c) => ({
    id: `${pack.id}:${c.id}`,
    entityType: c.entityType ?? "word",
    frequency: c.frequency,
    topic: c.topic ?? pack.topic,
    ageGroup: c.ageGroup ?? pack.ageGroup,
    level: c.level ?? pack.level,
    packId: pack.id,
  }));

  const forms: ConceptForm[] = [];
  for (const c of eligible) {
    const conceptId = `${pack.id}:${c.id}`;
    for (const lang of [target.pair.source, target.pair.target]) {
      const texts = c.forms[lang];
      if (!texts?.length) continue;
      forms.push({
        key: formKey(conceptId, lang),
        conceptId,
        lang,
        texts,
      });
    }
  }

  let cards = 0;
  const deckId = target.deckId ?? crypto.randomUUID();
  await db.transaction(
    "rw",
    [db.decks, db.concepts, db.conceptForms, db.cardStates, db.kv, db.outbox],
    async () => {
      const existingDeck = target.deckId
        ? await db.decks.get(target.deckId)
        : undefined;
      if (target.deckId && !existingDeck && !target.createDeck) {
        throw new Error("pack target deck does not exist");
      }
      if (
        existingDeck?.sourceLang &&
        existingDeck.targetLang &&
        (existingDeck.sourceLang !== target.pair.source ||
          existingDeck.targetLang !== target.pair.target)
      ) {
        throw new Error("pack direction does not match the target deck");
      }
      if (!existingDeck) {
        await db.decks.put({
          id: deckId,
          name: uiLang ? localizedPackName(pack, uiLang) : pack.name,
          createdAt: Date.now(),
          sourceLang: target.pair.source,
          targetLang: target.pair.target,
          topic: pack.topic,
          ageGroup: pack.ageGroup,
          level: pack.level,
        });
        await enqueueInTx("decks", "upsert", deckId);
      } else if (!existingDeck.sourceLang || !existingDeck.targetLang) {
        await db.decks.update(deckId, {
          sourceLang: target.pair.source,
          targetLang: target.pair.target,
        });
        await enqueueInTx("decks", "upsert", deckId);
      }

      await db.concepts.bulkPut(concepts);
      await db.conceptForms.bulkPut(forms);
      for (const concept of concepts) {
        await enqueueInTx("concepts", "upsert", concept.id);
      }
      for (const form of forms) {
        await enqueueInTx("conceptForms", "upsert", form.key);
      }

      // Only concepts with no state in this direction get one: re-installing a
      // pack must not reset the reviews the user has already earned on it.
      const keys = concepts.map((c) => cardStateKey(c.id, direction));
      const existingRows = (await db.cardStates.bulkGet(keys)).filter(
        (state): state is CardState => Boolean(state),
      );
      const existing = new Set(existingRows.map((state) => state.key));
      for (const state of existingRows) {
        if (state.deckId !== deckId) {
          await db.cardStates.update(state.key, { deckId });
          await enqueueInTx("cardStates", "upsert", state.key);
        }
      }
      const now = Date.now();
      const states: CardState[] = concepts
        .filter((c) => !existing.has(cardStateKey(c.id, direction)))
        .map((c) => ({
          key: cardStateKey(c.id, direction),
          conceptId: c.id,
          direction,
          deckId,
          nextReview: now,
          interval: 0,
          repetition: 0,
          efactor: 2.5,
        }));
      await db.cardStates.bulkPut(states);
      for (const state of states) {
        await enqueueInTx("cardStates", "upsert", state.key);
      }
      cards = states.length;

      const installed = await listInstalledPacks();
      const without = installed.filter((p) => p.id !== pack.id);
      without.push({
        id: pack.id,
        version: pack.version,
        name: uiLang ? localizedPackName(pack, uiLang) : pack.name,
        installedAt: Date.now(),
        langs: [target.pair.source, target.pair.target],
        deckId,
        direction,
        ownsDeck: !existingDeck,
      });
      await writeInstalledPacks(without);
    },
  );

  scheduleFlush();
  return {
    concepts: concepts.length,
    forms: forms.length,
    cards,
    deckId,
    direction,
    cardKeys: concepts.map((concept) => cardStateKey(concept.id, direction)),
  };
}

/**
 * Gives a home to packs installed before packs belonged to a dictionary at
 * all. One deck per pack rather than one shared bucket: the shelf then still
 * reads by topic and level, and the words keep the reviews they have earned.
 *
 * Idempotent — a pack that already records a deck is skipped, so this may run
 * on every launch.
 */
export async function adoptOrphanPacks(): Promise<number> {
  const installed = await listInstalledPacks();
  const orphans = installed.filter((entry) => !entry.deckId);
  if (orphans.length === 0) return 0;

  let adopted = 0;
  for (const entry of orphans) {
    const conceptIds = await db.concepts.where("packId").equals(entry.id).primaryKeys();
    if (conceptIds.length === 0) continue;
    const deckId = crypto.randomUUID();
    const now = Date.now();
    let didAdopt = false;
    await db.transaction(
      "rw",
      [db.decks, db.concepts, db.conceptForms, db.cardStates, db.kv, db.outbox],
      async () => {
      const rows = await listInstalledPacks();
      const current = rows.find((row) => row.id === entry.id);
      if (!current || current.deckId) return;
      const states = await db.cardStates.where("conceptId").anyOf(conceptIds).toArray();
      const fallbackDirection =
        current.langs[1] && current.langs[0]
          ? makeDirection(current.langs[1], current.langs[0])
          : undefined;
      const direction = current.direction ?? states[0]?.direction ?? fallbackDirection;
      if (!direction) return;
      const [from, to] = parseDirection(direction);
      await db.decks.put({
        id: deckId,
        name: current.name,
        createdAt: current.installedAt || now,
        sourceLang: from,
        targetLang: to,
      });
      const seen = new Set(states.map((state) => state.conceptId));
      // Existing progress keeps its schedule and only gains a home; concepts
      // never drilled get a fresh state so the deck is not empty on screen.
      const adoptedStates: CardState[] = [
        ...states.filter((state) => !state.deckId).map((state) => ({ ...state, deckId })),
        ...conceptIds
          .filter((id) => !seen.has(id as string))
          .map((id) => ({
            key: cardStateKey(id as string, direction),
            conceptId: id as string,
            direction,
            deckId,
            nextReview: now,
            interval: 0,
            repetition: 0,
            efactor: 2.5,
          })),
      ];
      await db.cardStates.bulkPut(adoptedStates);
      await writeInstalledPacks(
        rows.map((row) =>
          row.id === entry.id
            ? { ...row, deckId, direction, ownsDeck: true }
            : row,
        ),
      );
      await enqueueInTx("decks", "upsert", deckId);
      for (const conceptId of conceptIds) {
        await enqueueInTx("concepts", "upsert", conceptId as string);
      }
      const forms = await db.conceptForms
        .where("conceptId")
        .anyOf(conceptIds)
        .toArray();
      for (const form of forms) {
        await enqueueInTx("conceptForms", "upsert", form.key);
      }
      for (const state of adoptedStates) {
        await enqueueInTx("cardStates", "upsert", state.key);
      }
      didAdopt = true;
    });
    if (didAdopt) adopted += 1;
  }
  if (adopted > 0) scheduleFlush();
  return adopted;
}

/**
 * Remove a pack's concepts and forms. CardStates that reference removed
 * concepts are also dropped — they would be orphaned otherwise.
 *
 * Concepts backing a card the user filed into a deck are kept, together with
 * their forms and those CardStates: the store's `addCard` dedupes a personal
 * word onto a matching pack concept, so uninstalling a pack must not delete
 * words the user typed in themselves.
 */
export async function uninstallPack(packId: string): Promise<void> {
  await db.transaction(
    "rw",
    [db.concepts, db.conceptForms, db.cardStates, db.decks, db.kv, db.outbox],
    async () => {
      const installed = await listInstalledPacks();
      // The dictionary the pack filled. Being in *that* deck is not the user
      // adopting a word — every pack word is there. Being in a different one
      // is, which is what this has always tried to protect.
      const installedPack = installed.find((p) => p.id === packId);
      const ownDeckId = installedPack?.deckId;

      const conceptIds = await db.concepts.where("packId").equals(packId).primaryKeys();
      if (conceptIds.length > 0) {
        const states = await db.cardStates.where("conceptId").anyOf(conceptIds).toArray();
        const adopted = (state: CardState) => Boolean(state.deckId) && state.deckId !== ownDeckId;
        const keptConceptIds = new Set(states.filter(adopted).map((s) => s.conceptId));
        const removable = conceptIds.filter((id) => !keptConceptIds.has(id));
        const removedStates = states.filter((s) => !adopted(s));
        await db.cardStates.bulkDelete(removedStates.map((s) => s.key));
        for (const state of removedStates) {
          await enqueueInTx("cardStates", "delete", state.key);
        }
        if (removable.length > 0) {
          const removedForms = await db.conceptForms
            .where("conceptId")
            .anyOf(removable)
            .toArray();
          await db.concepts.bulkDelete(removable);
          await db.conceptForms.bulkDelete(removedForms.map((form) => form.key));
          for (const conceptId of removable) {
            await enqueueInTx("concepts", "delete", conceptId);
          }
          for (const form of removedForms) {
            await enqueueInTx("conceptForms", "delete", form.key);
          }
        }
      }

      // The deck was created for this pack, so it goes with it — unless the
      // user has since put their own words in it.
      if (ownDeckId && installedPack?.ownsDeck !== false) {
        const left = await db.cardStates.where("deckId").equals(ownDeckId).count();
        if (left === 0) {
          await db.decks.delete(ownDeckId);
          await enqueueInTx("decks", "delete", ownDeckId);
        }
      }

      await writeInstalledPacks(installed.filter((p) => p.id !== packId));
    },
  );
  scheduleFlush();
}
